import RequirePricingAdminUser from '@/components/auth/RequirePricingAdminUser';
import DateTimeDisplay from '@/components/DateTime';
import { formatITCModifierData } from '@/components/Pricing/ITCModifiers/SpecificITCModifier';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Column,
  Table,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  DomesticContentSetting,
  ItcModifierFormatType,
  ItcModifierSystemType,
  OrganizationEmailSettingCategories,
  OrganizationType,
  PriceSheetStatus,
} from 'types';

import {
  useGetOrganizationDetailsQuery,
  useGetOrganizationEmailSettingsQuery,
} from '../../../../../services/organizations';
import {
  useDeleteOrganizationDomesticContentMutation,
  useGetOrganizationDomesticContentQuery,
} from '../../../../../services/pricing';
import { TableLoading } from '../../../../TableLoading';

import { CreateDomesticContent } from './modals/CreateDomesticContent';

type DomesticContent = {
  id: string;
  name: string;
  organizationId: string;
  organizationName: string;
  state: string;
  values: DomesticContentSetting[];
};

export const DomesticContent = () => {
  const { alias = '', programType = '' } = useParams<{
    alias: string;
    programType: string;
  }>();
  const { isPhone } = useBreakpoint();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const [deleteOrganizationDomesticContentMutation] = useDeleteOrganizationDomesticContentMutation();
  const { data: emailSettings } = useGetOrganizationEmailSettingsQuery({ alias }, { skip: !alias });
  const { data: organization, isLoading: isOrganizationLoading } = useGetOrganizationDetailsQuery({ alias });
  const hasSignatoryEmail =
    (emailSettings?.emailSettings?.find((s) => s.category === OrganizationEmailSettingCategories.signatory)?.emails
      ?.length ?? 0) > 0;
  const isMaterialsVendor = organization?.organizationTypes?.includes(OrganizationType.materials);
  const { data, isLoading, isFetching } = useGetOrganizationDomesticContentQuery(
    { alias, programType },
    {
      skip: !alias || !programType,
    },
  );

  const domesticContentModifiers = useMemo(() => {
    if (!data || !data?.domesticContentModifiers?.length) {
      return [];
    }
    const dcModifiers = data.domesticContentModifiers.reduce((acc: any, ec: any) => {
      acc[ec.status] = acc[ec.status] || [];
      acc[ec.status].push(ec);
      return acc;
    }, {});
    const sortByCreatedDateDesc = (a: any, b: any) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();

    return [
      ...(dcModifiers[PriceSheetStatus.active] || []).toSorted(sortByCreatedDateDesc),
      ...(dcModifiers[PriceSheetStatus.archived] || []).toSorted(sortByCreatedDateDesc),
    ];
  }, [data]);
  const handleArchive = async (id: string) => {
    try {
      await deleteOrganizationDomesticContentMutation({
        alias,
        id,
        programType,
      }).unwrap();
      toast.success('Domestic Content Archived successfully');
    } catch (e) {
      console.error(e);
      toast.error('Error archiving Domestic Content');
    }
  };

  const columnConfig: Column[] = [
    {
      heading: 'status',
      dataKey: 'status',
      render: (cell: any) => (
        <Badge
          variant={
            cell === PriceSheetStatus.active ? 'success' : cell === PriceSheetStatus.archived ? 'warning' : 'danger'
          }
          message={cell}
        />
      ),
    },
    { heading: 'Name', dataKey: 'name' },
    { heading: 'org', dataKey: 'organizationName' },
    { heading: 'State', dataKey: 'state' },
    {
      heading: 'PV Only',
      render: (_cell: any, row: any) => {
        const pv = row?.values?.find((d: any) => d.systemType === ItcModifierSystemType.pvOnly);
        if (!pv || !pv?.modifier) return '';
        const netValue = pv.modifier.value - pv.holdback.value;
        const net =
          pv?.modifier?.format === ItcModifierFormatType.percent
            ? `${netValue.toFixed(2)}%`
            : `$${netValue.toFixed(2)}/W`;
        return (
          <Box childGap="sm">
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Modifier:</Box> {formatITCModifierData(pv.modifier)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Holdback: </Box>
              {formatITCModifierData(pv.holdback)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Net:</Box> {net}
            </Box>
          </Box>
        );
      },
    },
    {
      heading: 'Storage Only',
      render: (_cell: any, row: any) => {
        const st = row?.values?.find((d: any) => d.systemType === ItcModifierSystemType.storageOnly);
        if (!st || !st?.modifier) return '';
        const netValue = st.modifier.value - st.holdback.value;
        const net =
          st?.modifier?.format === ItcModifierFormatType.percent
            ? `${netValue.toFixed(2)}%`
            : `$${netValue.toFixed(2)}/W`;
        return (
          <Box childGap="sm">
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Modifier:</Box> {formatITCModifierData(st.modifier)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Holdback: </Box>
              {formatITCModifierData(st.holdback)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Net:</Box> {net}
            </Box>
          </Box>
        );
      },
    },
    {
      heading: 'PV and Storage',
      render: (_cell: any, row: any) => {
        const pvStor = row?.values?.find((d: any) => d.systemType === ItcModifierSystemType.pvAndStorage);
        if (!pvStor || !pvStor?.modifier) return '';
        const netValue = pvStor.modifier.value - pvStor.holdback.value;
        const net =
          pvStor?.modifier?.format === ItcModifierFormatType.percent
            ? `${netValue.toFixed(2)}%`
            : `$${netValue.toFixed(2)}/W`;
        return (
          <Box childGap="sm">
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Modifier:</Box> {formatITCModifierData(pvStor.modifier)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Holdback: </Box>
              {formatITCModifierData(pvStor.holdback)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Net:</Box> {net}
            </Box>
          </Box>
        );
      },
    },
    {
      heading: 'Created',
      dataKey: 'createdDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.createdDate} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: 'Archived',
      dataKey: 'archivedDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.archivedDate} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: '',
      render: (_cell: any, row: any) => {
        return (
          row.status === PriceSheetStatus.active && (
            <RequirePricingAdminUser>
              <Button
                size="sm"
                variant="secondary"
                tone="neutral"
                isDisabled={!hasSignatoryEmail}
                onClick={() => {
                  handleArchive(row.id);
                }}
              >
                Archive
              </Button>
            </RequirePricingAdminUser>
          )
        );
      },
    },
  ];
  return (
    <>
      {!hasSignatoryEmail && !isMaterialsVendor && (
        <Alert
          margin="lg 0 0"
          variant="warning"
          message="Domestic content settings cannot be edited without any signatory emails."
        />
      )}
      <CreateDomesticContent isModalOpen={isModalOpen} closeModal={closeModal} />
      <Card.Header>
        <RequirePricingAdminUser>
          <Box direction="row" justifyContent="space-between">
            <Box />
            <Button
              as="button"
              variant="primary"
              iconPrefix="add"
              size={{ base: 'sm', desktop: 'md' }}
              aria-label="create adder"
              isDisabled={!hasSignatoryEmail}
              onClick={() => openModal()}
            >
              {!isPhone && 'Domestic Content'}
            </Button>
          </Box>
        </RequirePricingAdminUser>
      </Card.Header>
      <Box margin="0 lg 0 lg">
        {isFetching || isLoading || isOrganizationLoading ? (
          <TableLoading rows={12} />
        ) : (
          <Table
            rowKey="id"
            columns={columnConfig}
            rows={domesticContentModifiers}
            isLoading={isLoading || isFetching || isOrganizationLoading}
            isScrollable={{ y: true }}
            hasStickyHeader
          />
        )}
      </Box>
    </>
  );
};
